import {
  createFileRoute,
  Outlet,
  redirect,
  useMatches,
} from "@tanstack/react-router"
import type { LucideIcon } from "lucide-react"

import { ApiError, AuthenticationService } from "@/client"
import { Footer } from "@/components/Common/Footer"
import { AppSidebar } from "@/components/Sidebar/AppSidebar"
import { Separator } from "@/components/ui/separator"
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar"

export const Route = createFileRoute("/_layout")({
  component: Layout,
  beforeLoad: async () => {
    try {
      // Verify authentication by calling the server
      await AuthenticationService.getCurrentUserInfo()
    } catch (error) {
      // Only authentication failures should force a login redirect. Rate-limit
      // responses are transient guard failures; keep the route mounted so
      // authenticated cookie requests can continue to resolve normally.
      if (error instanceof ApiError && error.status === 429) {
        return
      }

      if (error instanceof ApiError && [401, 403].includes(error.status)) {
        throw redirect({
          to: "/login",
        })
      }

      throw error
    }
  },
})

function Layout() {
  const matches = useMatches()
  const currentRoute = matches[matches.length - 1]
  const pageTitle = currentRoute?.staticData?.pageTitle as string | undefined
  const pageSubtitle = currentRoute?.staticData?.pageSubtitle as
    | string
    | undefined
  const PageIcon = currentRoute?.staticData?.pageIcon as LucideIcon | undefined
  const iconClassName =
    (currentRoute?.staticData?.pageIconClassName as string) ?? "text-primary"
  const iconStyle = currentRoute?.staticData?.pageIconStyle as
    | React.CSSProperties
    | undefined

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="bg-background sticky top-0 z-10 flex h-16 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1 text-muted-foreground" />
          {pageTitle && (
            <>
              <Separator orientation="vertical" className="mx-2 h-4" />
              <div className="flex items-center gap-2">
                {PageIcon && (
                  <PageIcon
                    className={`h-5 w-5 ${iconClassName}`}
                    style={iconStyle}
                  />
                )}
                <h1 className="text-lg font-semibold">{pageTitle}</h1>
                {pageSubtitle && (
                  <p className="text-muted-foreground">{pageSubtitle}</p>
                )}
              </div>
            </>
          )}
        </header>
        <main className="flex-1 p-6 md:p-8">
          <div className="mx-auto max-w-7xl">
            <Outlet />
          </div>
        </main>
        <Footer />
      </SidebarInset>
    </SidebarProvider>
  )
}

export default Layout
